/**
 * Shared PRO-637 RFx menu contract types. This file mirrors the backend
 * definitions field-for-field (source of truth: the backend API resources and
 * `App\Enums\*`). Downstream RFx-menu tasks consume these; no sibling task
 * redeclares them.
 *
 * The 403 `RfxForbiddenReason` and the guest `InviteState` are two DISTINCT
 * enums and must never be conflated.
 */

export type RfxRelationship = "owner" | "approver" | "panel_member" | "guest";

export const RFX_RELATIONSHIPS: readonly RfxRelationship[] = [
	"owner",
	"approver",
	"panel_member",
	"guest",
] as const;

/** Server-authoritative capability flags — the FE consumes, never derives, these. */
export interface RfxAccessFlags {
	can_view_procurer: boolean;
	can_score: boolean;
	can_view_other_scores: boolean;
	is_readonly_preview: boolean;
	relationship: RfxRelationship;
}

/**
 * The four stable, server-sent 403 `reason` codes, plus `"unknown"` — a
 * catch-all FE-only value (never sent by the server) that
 * `extractRfxForbiddenReason` returns for any 403 whose `reason` is missing
 * or not one of the four known codes (or any future server-added code the FE
 * hasn't been taught specific copy for yet — VT-043). `insufficient_role`
 * (VT-043's procurer-detail-block leg: a company user without the required
 * role hitting a procurer-only detail route) is a named member rather than
 * folding into `"unknown"` so it's explicit at the type level, even though
 * both currently resolve to the same redirect target. This keeps the
 * reason→redirect mapping TOTAL: every 403 from an RFx endpoint produces a
 * friendly redirect, never a silently-stuck skeleton.
 */
export type RfxForbiddenReason =
	| "not_panel_member"
	| "cross_company"
	| "revoked"
	| "insufficient_role"
	| "unknown";

export const RFX_FORBIDDEN_REASONS: readonly RfxForbiddenReason[] = [
	"not_panel_member",
	"cross_company",
	"revoked",
	"insufficient_role",
	"unknown",
] as const;

export interface RfxForbidden {
	status: false;
	title: "Forbidden";
	reason: RfxForbiddenReason;
	message: string;
}

/** Guest/panel invite lifecycle state — a SEPARATE enum from the 403 reason. */
export type InviteState = "pending" | "accepted" | "declined" | "expired";

export const INVITE_STATES: readonly InviteState[] = [
	"pending",
	"accepted",
	"declined",
	"expired",
] as const;

/** RFx list retrieval scopes served by `GET /api/v2/rfq`. */
export type RfxListScope = "tenders_quotes" | "approvals" | "panel";

export const RFX_LIST_SCOPES: readonly RfxListScope[] = [
	"tenders_quotes",
	"approvals",
	"panel",
] as const;

/**
 * Tab identifiers. The value is the exact route segment and response-map key —
 * a drift between this value and the backend `RfxListTab` value is a silent
 * empty-tab bug.
 */
export type RfxListTab =
	| "current"
	| "evaluation"
	| "awarded"
	| "drafts"
	| "archived"
	| "past";

export const RFX_LIST_TABS: readonly RfxListTab[] = [
	"current",
	"evaluation",
	"awarded",
	"drafts",
	"archived",
	"past",
] as const;

/** Tabs valid per scope — mirrors `App\Enums\RfxListScope::tabs()`. */
export const RFX_SCOPE_TABS: Record<RfxListScope, readonly RfxListTab[]> = {
	// Render order: live work first (Current, Evaluation, Awarded), then the two
	// dormant buckets. Mirrors `RfxListScope::tabs()` element-for-element.
	tenders_quotes: ["current", "evaluation", "awarded", "drafts", "archived"],
	approvals: ["current", "past"],
	panel: ["current", "past"],
} as const;

/**
 * Panel list row — mirrors `App\Http\Resources\RFx\RfxListItemResource`.
 * `rfx_id` + `rfx_evaluation_id` (snake_case) drive the Evaluation deep link.
 */
export interface RfxListItemResource {
	rfx_id: number;
	rfx_evaluation_id: number | null;
	uuid: string;
	name: string | null;
	status: number | null;
	status_label: string | null;
	submission_deadline: string | null;
	company_id: number | null;
}
